'use client'

import React from 'react'
import { Box, Flex, Stack, Text, Title } from '@mantine/core'
import { SkuGetStartedCta } from '../SkuGetStartedCta'
import { RefreshCtaButton } from '../RefreshCtaButton'
import { useBreakpoint } from '@/hooks/useBreakpoints'
import { staggerContainer, textFadeInVariantsNoTransition } from '@/lib/animations'
import { MotionBox, MotionStack } from '@/components/shared/AnimatedComponents'
import { getCategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'
import type { GetStartedCtaData } from '@/lib/hardcoded-data/CategoryPages/types'
import { GmCrossIcon } from '@/components/Icons/GmCrossIcon'
import type { CtaVariant } from '../RefreshCtaButton'
import type { CategoryWidgetProps } from '../types'
import { useUserContext } from '@/contexts/UserContext'
import { usePrescriptionRenewalStorefrontNavigation } from '@/hooks/usePrescriptionRenewalStorefrontNavigation'
import { PRESCRIPTION_RENEWAL_HREF } from '@/types/enums/sku-constants'

export default function WidgetGetStartedCta({
    skuPage = false,
    categoryName,
    data: dataProp,
    primaryService,
    primarySku,
    pageLocation,
    additionalInformation,
    skuPageVariant,
    fallbackVariant,
    prefetch,
}: CategoryWidgetProps & { fallbackVariant?: CtaVariant; prefetch?: boolean }) {
    const { isSm } = useBreakpoint()
    const data = (dataProp ?? getCategoryPageData(categoryName, 'getStartedCta')) as GetStartedCtaData | null
    const { user } = useUserContext()
    const isPrescriptionRenewal = data?.primaryCta?.href === PRESCRIPTION_RENEWAL_HREF
    const { href: prescriptionRenewalHref, isNavigating, navigateToPrescriptionRenewal } =
        usePrescriptionRenewalStorefrontNavigation(!!user && isPrescriptionRenewal)

    if (!data) return null

    const { heading, body, primaryCta, secondaryCta } = data
    const useConfiguredLinkCtas = Boolean(secondaryCta?.href && primaryCta.href)
    const primaryHref = isPrescriptionRenewal ? prescriptionRenewalHref : primaryCta.href

    return (
        <Box px={!skuPage ? { base: 16, sm: 24 } : 0}>
            <Box
                w="100%"
                maw={1440}
                mx="auto"
                style={{
                    background: '#2D1AFF',
                    borderRadius: 24,
                }}
            >
                <MotionStack
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-100px 0px' }}
                    variants={staggerContainer}
                    gap={24}
                    align={isSm ? 'flex-start' : 'center'}
                    ta={isSm ? 'left' : 'center'}
                    p={isSm ? '48px 24px' : '100px 48px'}
                >
                    {/* Main CTA section */}
                    <MotionBox variants={textFadeInVariantsNoTransition}>
                        <Stack gap={isSm ? 32 : 48} align={isSm ? 'flex-start' : 'center'} maw={800}>
                            <GmCrossIcon width={60} height={59} fill="#F5F1ED" />
                            <Stack gap={isSm ? 32 : 48} align={isSm ? 'flex-start' : 'center'}>
                                <Title
                                    order={2}
                                    c="white"
                                    fw={400}
                                    lh={{ base: 'var(--gm-refresh-title-md-lh)', sm: 'var(--gm-refresh-title-2xl-lh)' }}
                                    m={0}
                                    fz={{ base: 'var(--gm-refresh-title-md)', sm: 'var(--gm-refresh-title-2xl)' }}
                                    lts={isSm ? -1.6 : -2.1}
                                    mt={isSm ? 8 : -12}
                                >
                                    {heading}
                                </Title>
                                {body && (
                                    <Text
                                        component="div"
                                        c="white"
                                        size="md"
                                        lh="130%"
                                        m={0}
                                        ta={isSm ? 'left' : 'center'}
                                        fw={450}
                                        dangerouslySetInnerHTML={{ __html: body }}
                                        styles={{
                                            root: {
                                                '& b, & strong': { fontWeight: 600 },
                                                '& p': { display: 'block', margin: 0, marginBottom: '0.5em' },
                                                '& p:last-child': { marginBottom: 0 },
                                            },
                                        }}
                                    />
                                )}
                                {useConfiguredLinkCtas ? (
                                    <Flex
                                        gap={24}
                                        direction={{ base: 'column', sm: 'row' }}
                                        align={{ base: 'flex-start', sm: 'center' }}
                                        wrap="wrap"
                                    >
                                        <RefreshCtaButton
                                            href={primaryHref}
                                            variant="primary-white"
                                            prefetch={prefetch}
                                            aria-busy={
                                                isPrescriptionRenewal && isNavigating ? true : undefined
                                            }
                                            style={
                                                isPrescriptionRenewal && isNavigating
                                                    ? { cursor: 'wait' }
                                                    : undefined
                                            }
                                            onClick={async (event) => {
                                                if (isPrescriptionRenewal) {
                                                    await navigateToPrescriptionRenewal(event)
                                                }
                                            }}
                                            styles={{
                                                root: {
                                                    background: 'var(--gm-background-bg-solid, #f5f1ed)',
                                                },
                                            }}
                                        >
                                            {primaryCta.label}
                                        </RefreshCtaButton>
                                        <RefreshCtaButton
                                            href={secondaryCta!.href}
                                            variant="outline"
                                            prefetch={prefetch}
                                            c="#e8e5e3"
                                            styles={{
                                                root: {
                                                    background: 'transparent',
                                                    borderColor: '#e8e5e3',
                                                },
                                            }}
                                        >
                                            {secondaryCta!.label}
                                        </RefreshCtaButton>
                                    </Flex>
                                ) : (
                                    <SkuGetStartedCta
                                        sku={primarySku}
                                        service={primaryService}
                                        theme="primary-white"
                                        label={primaryCta.label}
                                        fallbackHref={primaryCta.href}
                                        fallbackLabel={primaryCta.label}
                                        fallbackVariant={fallbackVariant}
                                        pageLocation={pageLocation}
                                        additionalInformation={additionalInformation}
                                        skuPageVariant={skuPageVariant}
                                        prefetch={prefetch}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </MotionBox>
                </MotionStack>
            </Box>
        </Box>
    )
}
